import React, { memo, useContext, useMemo } from 'react'
import { Team } from './types'
import { OneXTwoContext } from './WithOneXTwoBet'
import betStatus from '@root/bet_utils/presenter/bet_status'
import I18n from 'i18n-js'

export interface PickButtonProps {
  team?: Team,
  picked?: boolean,
  setPicked: () => void,
  canPick: boolean,
  noTeamDisplay?: boolean,
  betIsAlreadyScored: boolean,
  wasCorrectlyPicked: boolean,
  variant: 'home' | 'away' | 'draw'
}

const TeamDisplay: React.FC<{ team: Team }> = ({ team }) => {
  return <div className="oneXtwo__team">
    {team.flag &&
      <img src={team.flag} />}
    <h3>{team.label}</h3>
  </div>
}

const TeamTBD: React.FC = () => {
  return <TeamDisplay team={{ label: I18n.t('onextwo.team_tbd'), uid: "__tbd"}} />
}

const _PickButton: React.FC<PickButtonProps> = ({ setPicked, noTeamDisplay, variant, picked, team, canPick, wasCorrectlyPicked, betIsAlreadyScored }) => {
  const classname = [
    "oneXtwo__pickButton",
    `oneXtwo__pickButton--${variant}`,
    canPick ? "oneXtwo__pickButton--canPick" : "oneXtwo__pickButton--cantPick", 
    picked ? "oneXtwo__pickButton--picked" : "oneXtwo__pickButton--notPicked",
    betIsAlreadyScored && picked && wasCorrectlyPicked ? "oneXtwo__pickButton--pickedAndCorrect" : "",
    betIsAlreadyScored && picked && !wasCorrectlyPicked ? "oneXtwo__pickButton--pickedAndWrong" : "",
  ].join(" ")

  const buttonLabel = variant === 'draw'
    ? I18n.t('onextwo.select_draw')
    : team
      ? I18n.t('onextwo.select_team_wins', {team: team.label})
      : I18n.t('onextwo.select_fallback')

  return <div className={classname}>
    <div className="oneXtwo__pickButton__teamHolder">
      {noTeamDisplay
        ? null
        : (team
          ? <TeamDisplay team={team} />
          : <TeamTBD />
        )}
    </div>
    {canPick
      ? <a className="oneXtwo__pickButton__button" onClick={setPicked}><span>{buttonLabel}</span></a>
      : <span className="oneXtwo__pickButton__button"><span>{buttonLabel}</span></span>}
    {variant !== 'draw' &&
      <div className="oneXtwo__pickButton__fullDeco" />}
  </div>
}

const PickButton = memo(_PickButton)

export const HomePickButton: React.FC = () => {
  const { bet, changePicks } = useContext(OneXTwoContext)
  const canPick = betStatus.canEdit(bet)

  const props = useMemo<PickButtonProps>(() => ({
    team: bet.teams.home,
    canPick,
    setPicked: () => changePicks(['home']),
    variant: 'home',
    wasCorrectlyPicked: bet.actual_result === 'home',
    betIsAlreadyScored: !!bet.actual_result,
    picked: (bet.picks || []).indexOf('home') > -1,
  }), [canPick, bet.picks, changePicks, bet.actual_result, bet.teams.home])

  return <PickButton {...props} />
}

export const AwayPickButton: React.FC = () => {
  const { bet, changePicks } = useContext(OneXTwoContext)
  const canPick = betStatus.canEdit(bet)

  const props = useMemo<PickButtonProps>(() => ({
    team: bet.teams.away,
    canPick,
    setPicked: () => changePicks(['away']),
    variant: 'away',
    wasCorrectlyPicked: bet.actual_result === 'away',
    betIsAlreadyScored: !!bet.actual_result,
    picked: (bet.picks || []).indexOf('away') > -1,
  }), [canPick, bet.picks, changePicks, bet.actual_result, bet.teams.away])

  return <PickButton {...props} />
}

export const DrawPickButton: React.FC = () => {
  const { bet, changePicks } = useContext(OneXTwoContext)
  const canPick = betStatus.canEdit(bet)

  const props = useMemo<PickButtonProps>(() => ({
    canPick,
    noTeamDisplay: true,
    setPicked: () => changePicks(['draw']),
    variant: 'draw',
    wasCorrectlyPicked: bet.actual_result === 'draw',
    betIsAlreadyScored: !!bet.actual_result,
    picked: (bet.picks || []).indexOf('draw') > -1,
  }), [canPick, bet.picks, changePicks, bet.actual_result])

  return <PickButton {...props} />
}

export default PickButton
